// victoria-client/src/views/items/detail/stock-chart/StockChart.tsx
import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { useNodeOrders } from "@/api/planningRun";
import { StockChartSkeleton } from "@/components/ChartSkeleton";
import { TimeseriesCanvas } from "@/components/timeseries-chart/TimeseriesCanvas";
import type { ViewRange } from "@/components/timeseries-chart/types";
import { Toggle } from "@/components/ui/toggle";
import { cn } from "@/lib/utils";

import {
  buildStockDatasets,
  SEVERITY_SWATCH,
  type OptionalLayer,
} from "./datasets";
import { planMarksPlugin, type PlanMarksOptions } from "./planMarks";
import { useStockChartData } from "./useStockChartData";

const DAY_MS = 24 * 60 * 60 * 1000;
const PLUGINS = [planMarksPlugin];

type StockChartProps = {
  itemId: number;
  locationId: number;
  /** When present, the chart shows this run's plan layers. */
  runId?: number;
  /** In a run: highlight this order and offer to leave it out of the plan. */
  orderId?: number;
  /** Without a run: project from this day instead of today. */
  asOf?: string;
  height?: number;
};

function Swatch({ className }: { className: string }) {
  return <span className={cn("inline-block w-4 border-t-2", className)} />;
}

export default function StockChart({
  itemId,
  locationId,
  runId,
  orderId,
  asOf,
  height = 420,
}: StockChartProps) {
  const { t } = useTranslation();
  const [includeOrder, setIncludeOrder] = useState(true);
  const {
    historyQuery,
    projectionQuery,
    notPlanned,
    history,
    withoutPlan,
    plan,
    dataSpan,
    defaultView,
    viewRange,
    setViewRange,
  } = useStockChartData(
    itemId,
    locationId,
    runId,
    includeOrder ? undefined : orderId,
    asOf,
  );
  const ordersQuery = useNodeOrders(runId ?? 0, itemId, locationId, {
    enabled: plan !== null,
  });
  const [shown, setShown] = useState<Set<OptionalLayer>>(
    () => new Set(["firmed"]),
  );

  const labels = useMemo(
    () => ({
      history: t("items.stockChart.onHand"),
      since: t("items.stockChart.since"),
      withoutPlan: t("items.stockChart.withoutPlan"),
      firmed: t("items.stockChart.firmed"),
      fullPlan: t("items.stockChart.fullPlan"),
      safetyStock: t("items.stockChart.safetyStock"),
      shortage: t("items.stockChart.shortage"),
      stockout: t("items.stockChart.stockout"),
    }),
    [t],
  );
  const startDate = projectionQuery.data?.startDate;
  const data = useMemo(
    () =>
      buildStockDatasets({
        history,
        startDate,
        withoutPlan,
        plan,
        shown,
        labels,
      }),
    [history, startDate, withoutPlan, plan, shown, labels],
  );
  const since = data.some((d) => d.label === labels.since);
  const view: ViewRange = viewRange ?? defaultView;

  const fence = plan?.timeFence ?? null;
  const pluginOptions = useMemo(() => {
    const planMarks: PlanMarksOptions = {
      startDate,
      fence,
      orders: (ordersQuery.data?.results ?? []).map((o) => ({
        date: o.dueDate,
        status: o.status,
        highlight: o.id === orderId,
      })),
    };
    return { planMarks };
  }, [ordersQuery.data, startDate, fence, orderId]);

  const toggle = (layer: OptionalLayer) =>
    setShown((prev) => {
      const next = new Set(prev);
      if (!next.delete(layer)) next.add(layer);
      return next;
    });

  return (
    <div className="flex flex-col gap-2">
      {notPlanned && (
        <p className="text-muted-foreground text-xs">
          {t("items.stockChart.notPlanned", { id: runId })}
        </p>
      )}
      <TimeseriesCanvas
        data={{ datasets: data }}
        dataSpan={dataSpan}
        viewRange={view}
        yAxisLabel={t("items.stockChart.axis")}
        minRangeMs={14 * DAY_MS}
        todayValue={startDate}
        isLoading={historyQuery.isLoading || projectionQuery.isLoading}
        skeleton={<StockChartSkeleton height={String(height)} />}
        onUserRange={setViewRange}
        enablePan
        height={height}
        plugins={PLUGINS}
        pluginOptions={pluginOptions}
      />
      {plan && (
        <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
          {(["withoutPlan", "firmed"] as const).map((layer) => (
            <Toggle
              key={layer}
              size="sm"
              pressed={shown.has(layer)}
              onPressedChange={() => toggle(layer)}
              className="h-6 text-xs data-[state=off]:opacity-50"
            >
              <Swatch
                className={
                  layer === "firmed"
                    ? "border-indigo-600"
                    : "border-dashed border-slate-400"
                }
              />
              {labels[layer]}
            </Toggle>
          ))}
          {orderId && (
            <Toggle
              size="sm"
              pressed={includeOrder}
              onPressedChange={setIncludeOrder}
              className="h-6 text-xs data-[state=off]:opacity-50"
            >
              {t("items.stockChart.thisOrder")}
            </Toggle>
          )}
          <span className="flex items-center gap-1.5">
            <Swatch className="border-primary" />
            {labels.fullPlan}
          </span>
          {since && (
            <span className="flex items-center gap-1.5">
              <Swatch className="border-slate-300" />
              {labels.since}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Swatch className="border-dotted border-red-600" />
            {labels.safetyStock}
          </span>
          {(["shortage", "stockout"] as const).map((severity) => (
            <span key={severity} className="flex items-center gap-1.5">
              <span
                className={cn(
                  "inline-block h-2.5 w-4 rounded-sm",
                  SEVERITY_SWATCH[severity],
                )}
              />
              {labels[severity]}
            </span>
          ))}
          <span>{t("items.stockChart.orderMarkers")}</span>
          {fence && (
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-4 rounded-sm bg-[repeating-linear-gradient(135deg,#94a3b8_0_1px,transparent_1px_4px)]" />
              {t("items.stockChart.timeFence")}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
